import { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import './RichTextEditor.css';
import { 
  Bold, Italic, List, ListOrdered, Quote,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon,
  Image as ImageIcon, Undo, Redo, Heading, Code
} from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LinkModal } from './LinkModal';
import { ImageUploadModal } from './ImageUploadModal';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string, plainText?: string, wordCount?: number) => void;
  placeholder?: string;
  minWords?: number;
  customVariables?: string[];
}

export const RichTextEditor = ({ value, onChange, placeholder, minWords = 0, customVariables }: RichTextEditorProps) => {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState({ url: "", text: "" });
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [variableDropdownOpen, setVariableDropdownOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-primary underline cursor-pointer',
          },
        },
      }),
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Scrivi il tuo contenuto qui...',
      }),
      CharacterCount,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
      onChange(html, text, words);
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (variableDropdownOpen && !target.closest('.variable-dropdown-container')) {
        setVariableDropdownOpen(false);
      }
    };
    
    if (variableDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [variableDropdownOpen]);

  const openLinkModal = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    const linkMark = editor.getAttributes('link');
    
    setCurrentLink({
      url: linkMark.href || "",
      text: selectedText || ""
    });
    setLinkModalOpen(true);
  };

  const handleSaveLink = (url: string, text?: string) => {
    if (!editor) return;
    if (text) {
      editor.chain()
        .focus()
        .insertContent(`<a href="${url}">${text}</a>`)
        .run();
    } else {
      editor.chain()
        .focus()
        .setLink({ href: url })
        .run();
    }
  };

  const handleRemoveLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  };

  const handleInsertImage = (url: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  if (!editor) {
    return null;
  }

  const wordCount = editor.storage.characterCount.words();
  const characterCount = editor.storage.characterCount.characters();

  return (
    <div className="border-[3px] border-dark">
      {/* Toolbar */}
      <TooltipProvider>
        <div className="flex flex-wrap gap-1 p-2 border-b-[3px] border-dark bg-muted/30">
          {/* Bold */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('bold') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBold().run()}
                className="!p-2 transition-all"
              >
                <Bold className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Testo grassetto (Ctrl+B)</p>
            </TooltipContent>
          </Tooltip>

          {/* Italic */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('italic') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className="!p-2 transition-all"
              >
                <Italic className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Testo corsivo (Ctrl+I)</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Heading */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('heading', { level: 2 }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className="!p-2"
              >
                <Heading className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Titolo di sezione (H2)</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Bullet List */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('bulletList') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className="!p-2"
              >
                <List className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Elenco puntato</p>
            </TooltipContent>
          </Tooltip>

          {/* Ordered List */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('orderedList') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className="!p-2"
              >
                <ListOrdered className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Elenco numerato</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Align Left */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                className="!p-2"
              >
                <AlignLeft className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea a sinistra</p>
            </TooltipContent>
          </Tooltip>

          {/* Align Center */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                className="!p-2"
              >
                <AlignCenter className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea al centro</p>
            </TooltipContent>
          </Tooltip>

          {/* Align Right */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                className="!p-2"
              >
                <AlignRight className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea a destra</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Blockquote */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('blockquote') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className="!p-2"
              >
                <Quote className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Citazione</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Link */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('link') ? 'primary' : 'outline'}
                onClick={openLinkModal}
                className="!p-2"
              >
                <LinkIcon className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Inserisci/modifica link</p>
            </TooltipContent>
          </Tooltip>

          {/* Image */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setImageModalOpen(true)}
                className="!p-2"
              >
                <ImageIcon className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Inserisci immagine</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Insert Variable */}
          {customVariables && customVariables.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative variable-dropdown-container">
                  <BrutalButton
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVariableDropdownOpen(!variableDropdownOpen);
                    }}
                    className="!p-2"
                  >
                    <Code className="w-4 h-4" />
                  </BrutalButton>
                  
                  {variableDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-56 border-[3px] border-dark bg-card shadow-[6px_6px_0px_0px] shadow-dark z-50 max-h-64 overflow-y-auto">
                      {customVariables.map((variable) => (
                        <button
                          key={variable}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            editor?.chain().focus().insertContent(`{{${variable}}}`).run();
                            setVariableDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-primary/10 transition-colors border-b-2 border-dark last:border-b-0 font-mono text-sm"
                        >
                          {`{{${variable}}}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Inserisci variabile utente</p>
              </TooltipContent>
            </Tooltip>
          )}

          <div className="w-[2px] bg-dark mx-1" />

          {/* Undo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="!p-2"
              >
                <Undo className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Annulla modifica</p>
            </TooltipContent>
          </Tooltip>

          {/* Redo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="!p-2"
              >
                <Redo className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Ripeti modifica</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="tiptap-editor bg-background"
      />

      {/* Word Count */}
      <div className="flex items-center justify-between p-2 border-t-[3px] border-dark bg-muted/30 text-sm">
        <span className={minWords && wordCount < minWords ? 'text-destructive font-medium' : 'text-muted-foreground'}>
          Parole: {wordCount} {minWords > 0 && `/ ${minWords} minimo`}
        </span>
        <span className="text-muted-foreground">
          Caratteri: {characterCount}
        </span>
      </div>

      {/* Modals */}
      <LinkModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onSave={handleSaveLink}
        onRemove={handleRemoveLink}
        initialUrl={currentLink.url}
        initialText={currentLink.text}
        canRemove={!!currentLink.url}
      />

      <ImageUploadModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        onInsert={handleInsertImage}
      />
    </div>
  );
};
